import { useState } from "react";
import { Images, FolderDown, Loader2, ImageOff } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { ImageLightbox } from "./ImageLightbox";
import { useAuthedBlobUrl, useNearViewport } from "../hooks/useAuthedImage";
import { downloadRendersZip, renderFilename, renderImagePath, errorText } from "../lib/api";

const STRIP = [
  { view: "front", label: "Front" },
  { view: "back", label: "Back" },
  { view: "left", label: "Left" },
  { view: "right", label: "Right" },
];

const Thumb = ({ creation, view, label, onOpen, near }) => {
  const { src, error, loading } = useAuthedBlobUrl(renderImagePath(creation, view, true), near);
  return (
    <li data-testid={`render-thumb-${view}`} className="snap-start shrink-0 w-28 sm:w-auto rounded-2xl overflow-hidden border border-border bg-[#f6f4ef] dark:bg-muted">
      <button
        type="button"
        onClick={onOpen}
        disabled={!src}
        data-testid={`render-open-${view}`}
        aria-label={`Enlarge the ${label.toLowerCase()} AI render`}
        className="block w-full aspect-square hover:opacity-90 transition-opacity disabled:cursor-default"
      >
        {src ? (
          <img src={src} alt={`${label} AI render`} loading="lazy" decoding="async" className="w-full h-full object-cover" />
        ) : (
          <span className="grid h-full w-full place-items-center text-muted-foreground">
            {error ? <ImageOff className="h-5 w-5" data-testid={`render-thumb-error-${view}`} /> : loading || near ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
          </span>
        )}
      </button>
      <p className="text-[10px] font-mono2 uppercase tracking-widest text-center py-1 bg-background/90">{label}</p>
    </li>
  );
};

// Step 03: the four AI views (front, back, left, right) the sculptor was built from — small cached thumbnails, a zoom
// viewer that steps through them, and a one-click ZIP of every available render (incl. the face close-up).
export const RenderStrip = ({ creation }) => {
  const [idx, setIdx] = useState(-1);
  const [zip, setZip] = useState(null); // null | percent | "busy"
  const [ref, near] = useNearViewport();
  const assets = creation?.render_assets || {};
  const views = STRIP.filter((v) => assets[v.view]);
  if (!views.length) return null;
  const total = views.length + (assets.closeup ? 1 : 0);

  const downloadAll = async () => {
    if (zip !== null) return;
    setZip("busy");
    try {
      await downloadRendersZip(creation, (pct) => setZip(pct === null ? "busy" : pct));
      toast.success(`Saved ${total} AI render${total === 1 ? "" : "s"} as a ZIP.`);
    } catch (e) {
      toast.error(errorText(e, "Could not build the ZIP of your renders — please try again."));
    } finally {
      setZip(null);
    }
  };

  const images = views.map((v) => ({ src: renderImagePath(creation, v.view), authed: true, label: v.label, filename: renderFilename(creation, v.view) }));
  return (
    <div ref={ref} data-testid="render-strip" className="rounded-3xl border border-border bg-card/60 p-3">
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <p className="flex items-center gap-2 text-xs font-mono2 uppercase tracking-widest text-muted-foreground">
          <Images className="h-4 w-4" /> AI renders · {views.length} view{views.length === 1 ? "" : "s"}
        </p>
        <Button data-testid="render-download-all-btn" size="sm" variant="outline" className="rounded-full h-9 gap-1.5 font-semibold" onClick={downloadAll} disabled={zip !== null} aria-live="polite">
          {zip === null ? <FolderDown className="h-4 w-4" /> : <Loader2 className="h-4 w-4 animate-spin" />}
          {zip === null ? `Download all images (${total})` : typeof zip === "number" ? `Downloading… ${zip}%` : "Preparing ZIP…"}
        </Button>
      </div>
      <ul data-testid="render-strip-list" className="flex gap-2 overflow-x-auto scrollbar-hide snap-x pb-1 sm:grid sm:grid-cols-4 sm:overflow-visible sm:pb-0">
        {views.map((v, i) => (
          <Thumb key={v.view} creation={creation} view={v.view} label={v.label} near={near} onOpen={() => setIdx(i)} />
        ))}
      </ul>
      <ImageLightbox
        open={idx >= 0}
        onOpenChange={(o) => !o && setIdx(-1)}
        images={images}
        index={Math.max(0, idx)}
        onIndexChange={setIdx}
        alt="AI render"
        onDownloadAll={downloadAll}
        downloadAllBusy={zip !== null}
      />
    </div>
  );
};
